import { useQuery } from '@tanstack/react-query';
import { API } from '@/utils/API';

export interface SubmissionActivityUser {
  id: string;
  first_name: string | null;
  last_name: string | null;
  email_address: string | null;
}

/** Hub actor when the event is not linked to an OV3 user (e.g. migration / Hub API). */
export interface HubAttributedUser {
  source_id: string;
  display_name: string;
}

export interface SubmissionActivityMetadata {
  document_id?: string;
}

export interface SubmissionActivity {
  id: string;
  details: string;
  created_at: string;
  title?: string;
  submission_id?: string;
  metadata?: SubmissionActivityMetadata | null;
  /**
   * Author of the activity. Populated for all rows the API returns;
   * declared optional only to keep the type tolerant of legacy/system
   * rows where the association is unexpectedly missing — UI falls back
   * to "—" in that case.
   */
  user?: SubmissionActivityUser | null;
  /** Present when `user` is absent and OV3 stored Hub attribution on the row. */
  hub_attributed_user?: HubAttributedUser | null;
}

interface UseSubmissionActivitiesReturn {
  activities: SubmissionActivity[];
  isLoading: boolean;
  error: string | null;
  refetch: () => void;
  clearError: () => void;
}

const fetchSubmissionActivities = async (
  submissionId: string
): Promise<SubmissionActivity[]> => {
  const response = await API.authenticated.fetch(
    `/submissions/${submissionId}/activities`
  );

  if (!response.ok) {
    throw new Error('Failed to fetch submission activities');
  }

  return response.json();
};

export const useSubmissionActivities = (
  submissionId: string | null
): UseSubmissionActivitiesReturn => {
  const {
    data: activities = [],
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['submission-activities', submissionId],
    queryFn: () => fetchSubmissionActivities(submissionId!),
    enabled: !!submissionId,
    staleTime: 5 * 60 * 1000, // Consider fresh for 5 minutes
    gcTime: 10 * 60 * 1000, // Keep in cache for 10 minutes
  });

  return {
    activities,
    isLoading,
    error: error ? (error as Error).message : null,
    refetch,
    clearError: () => {}, // Not needed with React Query
  };
};
